import React, { useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { OBJECT_TYPE } from "common/constants";
import { buildLink, getPath } from "common/routing";
import { getObjectDisplayForWorkflows } from "common-client/utils/customObject";
import {
  GetWorkflowsQuery,
  useGetWorkflowsQuery,
} from "../../../generated/graphql";
import { setTitle } from "../../../utils/title";
import { getCurrentURL } from "../../../utils/url";
import { AuthContext } from "../../Authorization/AuthContext";
import { FlexColumn, FlexRow } from "../../Common/__styles__/Layout";
import { ButtonLink } from "../../Common/ButtonLink";
import { Button } from "../../Common/Buttons/Button";
import { StyleVariant, Tag } from "../../Common/Tag";
import { Body, Title } from "../../Common/Typography";
import { Card } from "../__styles__/Card";
import { Loading } from "../__styles__/Guest";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { accountTitle } from "../utils";
import { WorkflowsPageContainer } from "./__styles__/WorkflowsPageContainer";

export const ListWorkflows = () => {
  const history = useHistory();
  const location = useLocation<{ prevLocation?: string }>();
  const { workflowDefinitionId } = useParams<{
    workflowDefinitionId: string;
  }>();

  const { data, loading } = useGetWorkflowsQuery({
    variables: { workflowDefinitionId },
  });

  const title = data?.workflowDefinition?.name ?? "Loading...";
  setTitle(`${title} | ${accountTitle()}`);

  if (loading) {
    return <Loading />;
  }

  if (!data?.workflowDefinition) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.WORKFLOW} />;
  }

  if (!data.workflows.length) {
    history.push(buildLink("workflowDefinition", { workflowDefinitionId }), {
      prevLocation: getCurrentURL(location),
    });
  }

  const prevLocation = location.state?.prevLocation ?? getPath("guestHome");
  return (
    <Layout hasGutters={true}>
      <WorkflowsPageContainer>
        <ButtonLink
          styleVariant="primary"
          size="medium"
          leftIconName="chevron-left"
          onClick={() => history.push(prevLocation)}
          style={{ width: "fit-content" }}
        >
          Back
        </ButtonLink>
        <FlexRow style={{ justifyContent: "space-between" }}>
          <Title size="large" type="semiBold">
            {data.workflowDefinition.name}
          </Title>
          <Button
            variant="secondary"
            size="small"
            iconStart="plus"
            onPress={() =>
              history.push(
                buildLink("createSubmission", {
                  submissionTypeId:
                    data.workflowDefinition?.initialSubmissionTypeId,
                }),
                { prevLocation: getCurrentURL(location) },
              )
            }
            title="New"
          />
        </FlexRow>
        <FlexColumn gap="m">
          <Body size="default" type="emphasis">
            Applications
          </Body>
          <FlexColumn>
            {data.workflows.map(workflow => (
              <WorkflowCard key={workflow.id} workflow={workflow} />
            ))}
          </FlexColumn>
        </FlexColumn>
      </WorkflowsPageContainer>
    </Layout>
  );
};

const WorkflowCard = ({
  workflow,
}: {
  workflow: GetWorkflowsQuery["workflows"][number];
}) => {
  const history = useHistory();
  const { account } = useContext(AuthContext);
  const objectDisplay =
    workflow.property ?
      getObjectDisplayForWorkflows({
        objectType: OBJECT_TYPE.PROPERTY,
        object: workflow.property,
        parcelFieldNameOverrides: account?.parcelFieldNameOverrides,
      })
    : null;

  return (
    <Card
      onClick={() =>
        history.push(buildLink("workflow", { workflowId: workflow.id }))
      }
    >
      <FlexColumn gap="xs" style={{ flex: 1, minWidth: 0 }}>
        {objectDisplay && (
          <Body
            size="default"
            type="emphasis"
            style={{ wordBreak: "break-word" }}
          >
            {objectDisplay}
          </Body>
        )}
        {workflow.secondaryDisplay && (
          <Body
            size="default"
            type="regular"
            color="contentSecondary"
            style={{ wordBreak: "break-word" }}
          >
            {workflow.secondaryDisplay}
          </Body>
        )}
      </FlexColumn>
      <Tag
        styleVariant={workflow.status.display as StyleVariant}
        style={{ flexShrink: 0 }}
      >
        {workflow.status.text}
      </Tag>
    </Card>
  );
};
